import React from 'react'
import PropTypes from 'prop-types'
import AppBadge from 'shared/badges/app-badge'

const HANDLE = 12
const CORNERS = [[-1, -1], [1, -1], [1, 1], [-1, 1]]

// Draws boxes over a page shown at `scale` screen pixels per page unit. Interactive overlays report pointer
// presses on a box, its corners and its rotate handle; the editor turns those into moves.
const BoxOverlay = ({ boxes, scale, labelFor, colorFor, selectedId, onPress }) => (
  <div className="position-absolute top-0 start-0 w-100 h-100" style={{ pointerEvents: onPress ? 'auto' : 'none' }}>
    {boxes.map((box) => {
      const color = colorFor(box)
      const selected = box.id === selectedId
      const press = (mode, corner) => (event) => {
        event.stopPropagation()
        onPress?.(event, box, mode, corner)
      }

      return (
        <div
          key={box.id}
          className={`position-absolute border border-2 border-${color} bg-${color} bg-opacity-10`}
          style={{
            left: (box.cx - box.w / 2) * scale,
            top: (box.cy - box.h / 2) * scale,
            width: box.w * scale,
            height: box.h * scale,
            transform: `rotate(${box.angle}deg)`,
            cursor: onPress ? 'move' : 'default',
            touchAction: 'none'
          }}
          onPointerDown={onPress ? press('move') : undefined}
          data-testid={onPress ? 'splitter-box' : undefined}
          data-selected={selected || undefined}
        >
          <AppBadge variant={color} className="position-absolute top-0 start-0 m-1">
            {labelFor(box)}
          </AppBadge>
          {selected &&
            CORNERS.map(([sx, sy]) => (
              <div
                key={`${sx}${sy}`}
                className={`position-absolute bg-white border border-2 border-${color}`}
                style={{
                  width: HANDLE,
                  height: HANDLE,
                  left: sx < 0 ? -HANDLE / 2 : undefined,
                  right: sx > 0 ? -HANDLE / 2 : undefined,
                  top: sy < 0 ? -HANDLE / 2 : undefined,
                  bottom: sy > 0 ? -HANDLE / 2 : undefined,
                  cursor: 'nwse-resize',
                  touchAction: 'none'
                }}
                onPointerDown={press('resize', [sx, sy])}
                data-testid={`splitter-resize-${sx}-${sy}`}
              />
            ))}
          {selected && (
            <div
              className={`position-absolute bg-white border border-2 border-${color} rounded-circle`}
              style={{ width: HANDLE + 4, height: HANDLE + 4, left: '50%', top: -28, marginLeft: -(HANDLE + 4) / 2, cursor: 'grab', touchAction: 'none' }}
              onPointerDown={press('rotate')}
              title="Drag to turn"
              data-testid="splitter-rotate-handle"
            />
          )}
        </div>
      )
    })}
  </div>
)

BoxOverlay.propTypes = {
  boxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  scale: PropTypes.number.isRequired,
  labelFor: PropTypes.func.isRequired,
  colorFor: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  onPress: PropTypes.func
}

export default BoxOverlay
